/**
 * UI-8d Task 6 — PlatformChangeDetailDrawer.
 *
 * Read-only side drawer for a single change-log entry (SOX-ITGC). The
 * change log records platform-wide schema / config / DEK rotation
 * changes; each row carries `beforeState` and `afterState` jsonb blobs
 * we want to surface side-by-side for audit reviewers.
 *
 * Composition:
 *   - Mounted only when `entry !== null`; the parent toggles by
 *     setting/clearing the selected row (same convention as
 *     `AuditLogDetailDrawer`).
 *   - Field-list section: implementedAt, changeType, description,
 *     requestedBy, approvedBy (— if absent), rollbackPlan (— if absent).
 *   - Two side-by-side `<SoxEvidenceCell>` panels for beforeState and
 *     afterState — reusing the UI-7d evidence renderer (collapsible
 *     `<details>` with pretty-printed JSON) keeps the diff layout
 *     consistent with the SOX control log.
 *
 * Accessibility: ESC closes, focus is trapped, focus is restored to the
 * row that opened the drawer on close. Same pattern as
 * AuditLogDetailDrawer / IncidentTimeline.
 */
import type { JSX } from 'react'
import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'

import type { ChangeEntry } from '../../hooks/usePlatformChangeLog.js'
import { useFocusTrap } from '../../hooks/useFocusTrap.js'
import { formatDateTime } from '../../utils/formatDate.js'
import { SoxEvidenceCell } from '../admin/compliance/SoxEvidenceCell.js'

export interface PlatformChangeDetailDrawerProps {
  entry: ChangeEntry | null
  onClose: () => void
}

export function PlatformChangeDetailDrawer({
  entry,
  onClose,
}: PlatformChangeDetailDrawerProps): JSX.Element | null {
  const open = entry !== null
  const trapRef = useFocusTrap<HTMLElement>(open)
  const previousFocus = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (open) {
      previousFocus.current = document.activeElement as HTMLElement
    } else if (previousFocus.current) {
      previousFocus.current.focus()
      previousFocus.current = null
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!entry) return null

  const dash = (s: string | null | undefined): string => s == null || s === '' ? '—' : s

  return (
    <>
      <div className="fixed inset-0 z-[9998] bg-black/40" onClick={onClose} />
      <aside
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-label="Change log entry"
        className="fixed right-0 top-0 z-[9999] flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 shrink-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-[#3454cf] flex items-center justify-center">
              <i className="pi pi-file-edit text-white text-sm" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Change Detail</h2>
              <p className="text-xs text-gray-400">{entry.changeType}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 flex items-center justify-center rounded-sm text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Details Card */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-50">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Details</h3>
            </div>
            <div className="p-5">
              <dl className="grid grid-cols-[8rem,1fr] gap-y-3 text-sm">
                <dt className="text-xs font-semibold text-gray-400 uppercase">Implemented</dt>
                <dd className="text-sm text-gray-800">{formatDateTime(entry.implementedAt)}</dd>
                <dt className="text-xs font-semibold text-gray-400 uppercase">Change type</dt>
                <dd className="text-sm text-gray-800">{entry.changeType}</dd>
                <dt className="text-xs font-semibold text-gray-400 uppercase">Description</dt>
                <dd className="text-sm text-gray-800 whitespace-pre-wrap">{entry.description}</dd>
                <dt className="text-xs font-semibold text-gray-400 uppercase">Requested by</dt>
                <dd className="text-sm font-mono text-gray-800 break-all">{entry.requestedBy}</dd>
                <dt className="text-xs font-semibold text-gray-400 uppercase">Approved by</dt>
                <dd className="text-sm font-mono text-gray-800 break-all">{dash(entry.approvedBy)}</dd>
                <dt className="text-xs font-semibold text-gray-400 uppercase">Rollback plan</dt>
                <dd className="text-sm text-gray-800 whitespace-pre-wrap">{dash(entry.rollbackPlan)}</dd>
              </dl>
            </div>
          </div>

          {/* Before/After */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-50">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide">Before State</h3>
              </div>
              <div className="p-4" data-testid="before-state">
                <SoxEvidenceCell evidence={entry.beforeState} />
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-50">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide">After State</h3>
              </div>
              <div className="p-4" data-testid="after-state">
                <SoxEvidenceCell evidence={entry.afterState} />
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  )
}

export default PlatformChangeDetailDrawer
